import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useSEO } from '../../utils/seo';
import Icon from '../../components/base/Icon';

const ABOUT_FORM_URL = 'https://readdy.ai/api/form/d6peh4ea739gopmlgps0';

const SERVICE_OPTIONS = [
  { value: '', label: 'Select a service...' },
  { value: 'Fence Installation & Repair', label: 'Fence Installation & Repair' },
  { value: 'Handyman Services', label: 'Handyman Services' },
  { value: 'Bathroom Remodels', label: 'Bathroom Remodels' },
  { value: 'Small Remodel Projects', label: 'Small Remodel Projects' },
  { value: 'Other', label: 'Other' },
];

interface ContactFormState {
  full_name: string;
  phone: string;
  email: string;
  service_needed: string;
  project_details: string;
}

const emptyForm = (): ContactFormState => ({
  full_name: '',
  phone: '',
  email: '',
  service_needed: '',
  project_details: '',
});

function AboutContactForm() {
  const [values, setValues] = useState<ContactFormState>(emptyForm());
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setValues((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const validate = (): string => {
    if (!values.full_name.trim()) return 'Full name is required.';
    if (!values.phone.trim()) return 'Phone number is required.';
    if (!values.email.trim()) return 'Email address is required.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) return 'Please enter a valid email address.';
    if (!values.service_needed) return 'Please select a service.';
    if (!values.project_details.trim()) return 'Please describe your project.';
    if (values.project_details.length > 500) return 'Project details must be 500 characters or fewer.';
    return '';
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const validationError = validate();
    if (validationError) {
      setErrorMsg(validationError);
      setStatus('error');
      return;
    }
    setStatus('submitting');
    setErrorMsg('');
    try {
      const params = new URLSearchParams();
      (Object.keys(values) as (keyof ContactFormState)[]).forEach((key) => {
        params.append(key, values[key]);
      });
      const res = await fetch(ABOUT_FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      if (!res.ok) throw new Error('Submission failed');
      setStatus('success');
      setValues(emptyForm());
    } catch {
      setStatus('error');
      setErrorMsg('Something went wrong. Please try again or call us at (208) 204-6331.');
    }
  };

  const inputClass = 'w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#B45309] focus:border-transparent text-sm';

  return (
    <form onSubmit={handleSubmit} data-readdy-form id="about-contact-form" noValidate>
      {status === 'success' && (
        <div className="mb-5 p-4 bg-green-50 border border-green-200 rounded-lg text-green-800 text-sm flex items-start gap-2" role="alert">
          <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5"><Icon name="check" className="w-4 h-4" /></span>
          <span>Thanks — we received your message and will be in touch within 24 hours.</span>
        </div>
      )}
      {status === 'error' && errorMsg && (
        <div className="mb-5 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-start gap-2" role="alert">
          <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5"><Icon name="circle-exclamation" className="w-4 h-4" /></span>
          <span>{errorMsg}</span>
        </div>
      )}
      <div className="grid md:grid-cols-2 gap-4 mb-4">
        <div>
          <label htmlFor="ac-full_name" className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
          <input type="text" id="ac-full_name" name="full_name" required value={values.full_name} onChange={handleChange} className={inputClass} placeholder="John Smith" />
        </div>
        <div>
          <label htmlFor="ac-phone" className="block text-sm font-medium text-gray-700 mb-1">Phone Number *</label>
          <input type="tel" id="ac-phone" name="phone" required value={values.phone} onChange={handleChange} className={inputClass} placeholder="(208) 555-0123" />
        </div>
      </div>
      <div className="mb-4">
        <label htmlFor="ac-email" className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
        <input type="email" id="ac-email" name="email" required value={values.email} onChange={handleChange} className={inputClass} placeholder="john@example.com" />
      </div>
      <div className="mb-4">
        <label htmlFor="ac-service_needed" className="block text-sm font-medium text-gray-700 mb-1">Service Needed *</label>
        <select id="ac-service_needed" name="service_needed" required value={values.service_needed} onChange={handleChange} className={`${inputClass} cursor-pointer`}>
          {SERVICE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>
      <div className="mb-6">
        <label htmlFor="ac-project_details" className="block text-sm font-medium text-gray-700 mb-1">Project Details *</label>
        <textarea id="ac-project_details" name="project_details" required rows={4} maxLength={500} value={values.project_details} onChange={handleChange} className={`${inputClass} resize-none`} placeholder="Tell us what you need help with..." />
        <p className="text-xs text-gray-500 mt-1">{values.project_details.length}/500 characters</p>
      </div>
      <button type="submit" disabled={status === 'submitting'} className="w-full bg-[#B45309] hover:bg-[#92400E] disabled:opacity-60 text-white px-6 py-4 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer inline-flex items-center justify-center gap-2">
        {status === 'submitting' ? 'Sending...' : (
          <>
            <span className="w-5 h-5 flex items-center justify-center"><Icon name="paper-plane" className="w-4 h-4" /></span>
            Send Message
          </>
        )}
      </button>
    </form>
  );
}

const serviceAreas = [
  { city: 'Boise', primary: true },
  { city: 'Meridian', primary: true },
  { city: 'Nampa', primary: true },
  { city: 'Caldwell', primary: true },
  { city: 'Eagle', primary: false },
  { city: 'Star', primary: false },
  { city: 'Kuna', primary: false },
  { city: 'Middleton', primary: false },
  { city: 'Garden City', primary: false },
  { city: 'Beacon Light', primary: false },
  { city: 'Emmett', primary: false },
  { city: 'Mountain Home', primary: false },
];

const whatWeDo = [
  { icon: 'border-all', label: 'Fence Installation & Repair', desc: 'Wood, vinyl, chain link, composite, gates, and post replacement.' },
  { icon: 'screwdriver-wrench', label: 'Handyman Services', desc: 'Drywall, painting, fixtures, doors, caulking, and general home maintenance.' },
  { icon: 'bath', label: 'Bathroom Remodels', desc: 'Full gut-and-rebuild, tile, vanities, walk-in showers, and lighting upgrades.' },
  { icon: 'hammer', label: 'Small Remodel Projects', desc: 'Kitchen updates, basement finishing, flooring, trim, and fix-and-flip support.' },
];

const whatWeDont = [
  { icon: 'house-chimney', label: 'Roofing', desc: 'We don\'t do roofing of any kind. We\'ll refer you to a trusted local roofer.' },
  { icon: 'bolt', label: 'Full Electrical Rewires', desc: 'We handle minor fixture swaps, but full panel work or rewires require a licensed electrician.' },
  { icon: 'droplet', label: 'Full Plumbing Installs', desc: 'We handle minor plumbing tasks, but full system installs require a licensed plumber.' },
  { icon: 'wind', label: 'HVAC Work', desc: 'Heating and cooling systems require a certified HVAC technician — not our scope.' },
];

const communicationStandards = [
  {
    icon: 'phone',
    title: 'We Answer or Call Back',
    desc: 'When you call, we pick up or return your call the same day. No voicemail black holes.',
  },
  {
    icon: 'file-lines',
    title: 'Written Estimates Only',
    desc: 'Every quote is itemized and in writing before any work begins. No verbal-only pricing.',
  },
  {
    icon: 'clock',
    title: 'We Tell You When We\'re Coming',
    desc: 'You\'ll get a confirmed start date and a heads-up if anything changes. We don\'t leave you guessing.',
  },
  {
    icon: 'message',
    title: 'Honest Scope Updates',
    desc: 'If we find something unexpected mid-job, we stop and talk to you before proceeding. No surprise invoices.',
  },
  {
    icon: 'circle-check',
    title: 'Walkthrough Before We Leave',
    desc: 'We walk through the finished work with you before we consider the job done. Your sign-off matters.',
  },
  {
    icon: 'envelope',
    title: 'Reachable by Phone or Email',
    desc: 'Call (208) 204-6331 or email Southernkole@gmail.com. We respond within one business day.',
  },
];

const jobsiteStandards = [
  { icon: 'broom', text: 'We clean up the job site at the end of every workday — no debris left behind.' },
  { icon: 'shield-halved', text: 'We treat your property with care. Tools and materials are staged neatly, not scattered.' },
  { icon: 'door-open', text: 'We ask before entering areas of your home beyond the work zone.' },
  { icon: 'user-check', text: 'We show up when we say we will. If something changes, we call ahead.' },
  { icon: 'volume-xmark', text: 'We keep noise and disruption to a minimum, especially in residential neighborhoods.' },
  { icon: 'lock', text: 'Gates, doors, and access points are secured at the end of each day.' },
];

export default function AboutPage() {
  useSEO({
    title: 'About Fix Right Services | Boise Fence, Handyman & Remodel Contractor',
    description: 'Meet Kole Southern and the Fix Right Services team. Licensed, insured Boise contractor serving Treasure Valley with fence installation, handyman services, and home remodels. No roofing.',
    keywords: 'about Fix Right Services, Kole Southern contractor Boise, licensed handyman Boise Idaho, fence contractor Treasure Valley',
    canonical: `${import.meta.env.VITE_SITE_URL || 'https://fixrightservices.com'}/about`,
    schema: {
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      'name': 'About Fix Right Services',
      'description': 'Licensed and insured Boise contractor specializing in fence installation, handyman services, and home remodels across the Treasure Valley.',
      'url': `${import.meta.env.VITE_SITE_URL || 'https://fixrightservices.com'}/about`,
      'mainEntity': {
        '@type': 'LocalBusiness',
        'name': 'Fix Right Services',
        'founder': { '@type': 'Person', 'name': 'Kole Southern' },
        'telephone': '+12082046331',
        'email': 'Southernkole@gmail.com',
        'address': {
          '@type': 'PostalAddress',
          'addressLocality': 'Boise',
          'addressRegion': 'ID',
          'addressCountry': 'US',
        },
        'areaServed': serviceAreas.map((a) => ({ '@type': 'City', 'name': a.city })),
      },
    },
  });

  return (
    <div className="min-h-screen bg-white">

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm shadow-sm" aria-label="Main navigation">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-20">
            <Link to="/">
              <img
                src="https://static.readdy.ai/image/c174e3e7fa028bcf803982b5ec8aa9e6/2ea97a783d40b0afc97fadf5f7236f78.webp"
                alt="Fix Right Services Logo"
                width="180"
                height="56"
                className="h-14 w-auto"
                loading="eager"
              />
            </Link>
            <div className="hidden md:flex items-center gap-6 text-sm font-medium text-gray-700 ml-8">
              <Link to="/" className="hover:text-[#B45309] transition-colors cursor-pointer">Home</Link>
              <Link to="/services" className="hover:text-[#B45309] transition-colors cursor-pointer">Services</Link>
              <Link to="/about" className="text-[#B45309] font-semibold cursor-pointer">About</Link>
              <Link to="/blog" className="hover:text-[#B45309] transition-colors cursor-pointer">Blog</Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden" aria-label="About hero">
        <div className="absolute inset-0 z-0">
          <img
            src="https://public.readdy.ai/ai/img_res/b09e6ee4ed91c7745d708cbc8b43bd2d.jpg"
            alt="Fix Right Services — About Us"
            width="1920"
            height="700"
            className="w-full h-full object-cover object-top"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/45 to-black/55"></div>
        </div>
        <div className="relative z-10 max-w-4xl mx-auto text-center w-full">
          <div className="inline-flex items-center gap-2 bg-[#B45309]/90 text-white text-sm font-medium px-4 py-2 rounded-full mb-6">
            <span className="w-4 h-4 flex items-center justify-center"><Icon name="location-dot" className="w-3.5 h-3.5" /></span>
            Boise, Idaho — Treasure Valley
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            About Fix Right Services
          </h1>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            A local Boise contractor built on honest communication, quality work, and showing up when we say we will.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="#contact"
              className="inline-flex items-center gap-2 bg-white hover:bg-gray-50 text-gray-900 px-8 py-3.5 rounded-lg text-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
            >
              <span className="w-5 h-5 flex items-center justify-center"><Icon name="envelope" className="w-4 h-4" /></span>
              Send a Message
            </a>
          </div>
        </div>
      </section>

      {/* Founder Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white" aria-label="About the founder">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <div className="w-full h-[480px] rounded-2xl overflow-hidden shadow-xl">
                <img
                  src="https://readdy.ai/api/search-image?query=friendly%20professional%20male%20contractor%20in%20his%2030s%20wearing%20a%20clean%20work%20shirt%20standing%20in%20a%20bright%20Boise%20Idaho%20residential%20backyard%20smiling%20confidently%20natural%20light%20warm%20tones%20approachable%20trustworthy%20local%20business%20owner%20portrait&width=700&height=900&seq=about-founder-01&orientation=portrait"
                  alt="Kole Southern — Owner, Fix Right Services"
                  width="700"
                  height="900"
                  className="w-full h-full object-cover object-top"
                  loading="lazy"
                />
              </div>
              <div className="absolute -bottom-5 -right-5 bg-[#B45309] text-white rounded-2xl px-6 py-4 shadow-lg">
                <p className="text-2xl font-bold leading-none">5★</p>
                <p className="text-xs text-white/85 mt-1">Rated by Treasure Valley homeowners</p>
              </div>
            </div>

            <div>
              <p className="text-[#B45309] font-semibold text-sm uppercase tracking-wider mb-3">Meet the Owner</p>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6 leading-tight">
                Kole Southern,<br />Owner &amp; Operator
              </h2>
              <div className="space-y-4 text-gray-600 leading-relaxed">
                <p>
                  Fix Right Services was founded by <strong>Kole Southern</strong>, a hands-on contractor based in the Treasure Valley. Kole started the business with a straightforward goal: give Boise homeowners a reliable option for fence work, handyman repairs, and focused remodel projects — without the runaround that comes with larger outfits.
                </p>
                <p>
                  Kole is on every job. That means when you call Fix Right Services, you&apos;re talking to the person who will actually show up at your property, assess the work, and see it through to completion. No subcontracting surprises, no miscommunication between crews.
                </p>
                <p>
                  Over the years, Kole has built a reputation across Boise, Meridian, Nampa, Caldwell, and surrounding communities for being reliable, honest, and easy to work with. The reviews speak for themselves — and so does the repeat business.
                </p>
              </div>
              <div className="mt-8 flex flex-wrap gap-4">
                <a
                  href="mailto:Southernkole@gmail.com"
                  className="inline-flex items-center gap-2 border border-gray-300 hover:border-[#B45309] text-gray-700 hover:text-[#B45309] px-6 py-3 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
                >
                  <span className="w-5 h-5 flex items-center justify-center"><Icon name="envelope" className="w-4 h-4" /></span>
                  Send an Email
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What We Do / Don't Do */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50" aria-label="Services scope">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">What We Do — and What We Don&apos;t</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              We&apos;d rather be honest about our scope than take on work we can&apos;t do well. Here&apos;s exactly what you can expect from Fix Right Services.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* What We Do */}
            <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <Icon name="circle-check" className="w-5 h-5 text-green-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">What We Do</h3>
              </div>
              <div className="space-y-4">
                {whatWeDo.map((item) => (
                  <div key={item.label} className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-[#B45309]/10 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Icon name={item.icon} className="w-5 h-5 text-[#B45309]" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 text-sm">{item.label}</p>
                      <p className="text-gray-500 text-sm mt-0.5">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-6 pt-6 border-t border-gray-100">
                <Link to="/services" className="inline-flex items-center gap-2 text-[#B45309] hover:text-[#92400E] font-semibold text-sm cursor-pointer">
                  See full services page
                  <Icon name="arrow-right" className="w-4 h-4" />
                </Link>
              </div>
            </div>

            {/* What We Don't Do */}
            <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-red-50 rounded-lg flex items-center justify-center">
                  <Icon name="circle-xmark" className="w-5 h-5 text-red-400" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">What We Don&apos;t Do</h3>
              </div>
              <div className="space-y-4">
                {whatWeDont.map((item) => (
                  <div key={item.label} className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Icon name={item.icon} className="w-5 h-5 text-gray-400" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-700 text-sm">{item.label}</p>
                      <p className="text-gray-500 text-sm mt-0.5">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-6 pt-6 border-t border-gray-100">
                <p className="text-sm text-gray-500">
                  Need a roofer or licensed trade? We&apos;re happy to point you toward a trusted local referral. Just ask.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white" aria-label="Service areas">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Where We Work</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Fix Right Services operates throughout Boise and the greater Treasure Valley. Primary coverage areas are listed below — call us if you&apos;re unsure about your location.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
            {serviceAreas.map((area) => (
              <div
                key={area.city}
                className={`flex items-center gap-3 rounded-xl px-5 py-4 border transition-shadow hover:shadow-sm ${area.primary ? 'bg-[#B45309]/5 border-[#B45309]/20' : 'bg-gray-50 border-gray-100'}`}
              >
                <span className={`w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0 ${area.primary ? 'bg-[#B45309]/15' : 'bg-gray-200'}`}>
                  <Icon name="location-dot" className={`w-4 h-4 ${area.primary ? 'text-[#B45309]' : 'text-gray-400'}`} />
                </span>
                <div>
                  <p className="font-semibold text-gray-900 text-sm">{area.city}</p>
                  <p className="text-xs text-gray-500">{area.primary ? 'Primary service area' : 'Available — call to confirm'}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="bg-[#B45309]/5 border border-[#B45309]/20 rounded-xl p-6 text-center">
            <p className="text-gray-700 text-sm">
              <strong>Don&apos;t see your city?</strong> We occasionally take projects outside our standard coverage area. Call{' '}
              <a href="tel:+12082046331" className="text-[#B45309] font-semibold hover:underline cursor-pointer">(208) 204-6331</a>{' '}
              and we&apos;ll let you know if we can help.
            </p>
          </div>
        </div>
      </section>

      {/* Credentials / Trust Block */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50" aria-label="Credentials and licensing">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Licensed &amp; Insured in Idaho</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              We operate fully within Idaho contractor licensing requirements. Here&apos;s how to verify our credentials and what our insurance covers.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Registration */}
            <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-12 h-12 bg-[#B45309]/10 rounded-xl flex items-center justify-center">
                  <Icon name="id-card" className="w-6 h-6 text-[#B45309]" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Idaho Contractor Registration</h3>
                  <p className="text-xs text-gray-500">Idaho Division of Building Safety</p>
                </div>
              </div>
              <div className="bg-gray-50 rounded-xl px-5 py-4 mb-5">
                <p className="text-xs text-gray-500 uppercase tracking-wider font-medium mb-1">Registration Number</p>
                <p className="text-2xl font-bold text-gray-900 tracking-wide">RCE-XXXXX</p>
                <p className="text-xs text-[#B45309] mt-1">⚠ Replace with your actual Idaho registration number</p>
              </div>
              <p className="text-sm text-gray-600 mb-5 leading-relaxed">
                Idaho requires contractors to register with the Division of Building Safety before performing work. You can verify our registration status directly on the state&apos;s public lookup tool.
              </p>
              <a
                href="https://dbs.idaho.gov/contractor-registration/"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="inline-flex items-center gap-2 text-[#B45309] hover:text-[#92400E] font-semibold text-sm cursor-pointer"
              >
                <span className="w-5 h-5 flex items-center justify-center"><Icon name="arrow-up-right-from-square" className="w-4 h-4" /></span>
                Verify on Idaho DBS Lookup
              </a>
            </div>

            {/* Insurance */}
            <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-12 h-12 bg-[#B45309]/10 rounded-xl flex items-center justify-center">
                  <Icon name="shield-halved" className="w-6 h-6 text-[#B45309]" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">General Liability Insurance</h3>
                  <p className="text-xs text-gray-500">Coverage summary</p>
                </div>
              </div>
              <div className="bg-gray-50 rounded-xl px-5 py-4 mb-5 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Coverage Type</span>
                  <span className="font-semibold text-gray-900">General Liability</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Coverage Area</span>
                  <span className="font-semibold text-gray-900">Idaho — Treasure Valley</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Certificate Available</span>
                  <span className="font-semibold text-green-700">Yes — on request</span>
                </div>
                <p className="text-xs text-[#B45309] pt-1">⚠ Replace with your actual insurance summary details</p>
              </div>
              <p className="text-sm text-gray-600 mb-5 leading-relaxed">
                Fix Right Services carries general liability insurance for all work performed in Idaho. We&apos;re happy to provide a Certificate of Insurance before any project begins — just ask.
              </p>
              <a
                href="tel:+12082046331"
                className="inline-flex items-center gap-2 text-[#B45309] hover:text-[#92400E] font-semibold text-sm cursor-pointer"
              >
                <span className="w-5 h-5 flex items-center justify-center"><Icon name="phone" className="w-4 h-4" /></span>
                Request Certificate of Insurance
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* How We Communicate */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white" aria-label="Communication standards">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">How We Communicate</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              One of the most common complaints about contractors is poor communication. Here&apos;s our standard — and what you can hold us to.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {communicationStandards.map((item) => (
              <div key={item.title} className="bg-gray-50 rounded-xl p-6 hover:shadow-sm transition-shadow border border-gray-100">
                <div className="w-11 h-11 bg-[#B45309]/10 rounded-lg flex items-center justify-center mb-4">
                  <Icon name={item.icon} className="w-5 h-5 text-[#B45309]" />
                </div>
                <h3 className="font-bold text-gray-900 text-sm mb-2">{item.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Jobsite Respect */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50" aria-label="Jobsite standards">
        <div className="max-w-5xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-[#B45309] font-semibold text-sm uppercase tracking-wider mb-3">Our Commitment</p>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">Jobsite Respect Standards</h2>
              <p className="text-gray-600 mb-8 leading-relaxed">
                Your home is not a construction site — it&apos;s where you live. We take that seriously. These are the standards we hold ourselves to on every job, every day.
              </p>
              <ul className="space-y-4">
                {jobsiteStandards.map((item) => (
                  <li key={item.text} className="flex items-start gap-4">
                    <span className="w-9 h-9 flex items-center justify-center bg-[#B45309]/10 rounded-lg flex-shrink-0 mt-0.5">
                      <Icon name={item.icon} className="w-4 h-4 text-[#B45309]" />
                    </span>
                    <p className="text-gray-700 text-sm leading-relaxed">{item.text}</p>
                  </li>
                ))}
              </ul>
            </div>
            <div className="w-full h-[480px] rounded-2xl overflow-hidden shadow-xl">
              <img
                src="https://readdy.ai/api/search-image?query=clean%20and%20organized%20residential%20construction%20work%20site%20in%20Boise%20Idaho%20with%20neatly%20stacked%20lumber%20tools%20arranged%20properly%20no%20debris%20on%20lawn%20professional%20contractor%20standards%20tidy%20backyard%20fence%20project%20in%20progress%20warm%20afternoon%20light&width=700&height=900&seq=about-jobsite-01&orientation=portrait"
                alt="Fix Right Services jobsite standards — clean and organized work"
                width="700"
                height="900"
                className="w-full h-full object-cover object-top"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-[#B45309]" aria-label="Call to action">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Ready to Work Together?</h2>
          <p className="text-xl text-white/90 mb-8 max-w-xl mx-auto">
            Call Kole directly or send a message below. Free estimates, honest pricing, and work you can count on.
          </p>
          <a
            href="tel:+12082046331"
            className="inline-flex items-center gap-3 bg-white hover:bg-gray-50 text-[#B45309] px-10 py-4 rounded-lg text-xl font-bold transition-colors whitespace-nowrap cursor-pointer shadow-lg"
          >
            <span className="w-6 h-6 flex items-center justify-center"><Icon name="phone" className="w-5 h-5" /></span>
            Call (208) 204-6331
          </a>
          <p className="text-white/70 text-sm mt-4">Mon–Fri 8am–6pm · Sat 9am–3pm · Typical response within 24 hours</p>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white" id="contact" aria-label="Contact form">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Send Us a Message</h2>
            <p className="text-gray-600">
              Prefer to write it out? Fill in the form and we&apos;ll get back to you within one business day.
            </p>
          </div>
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
            <AboutContactForm />
          </div>
          <div className="mt-6 flex flex-wrap justify-center gap-6 text-sm text-gray-500">
            <div className="flex items-center gap-2">
              <span className="w-5 h-5 flex items-center justify-center"><Icon name="phone" className="w-4 h-4 text-[#B45309]" /></span>
              <a href="tel:+12082046331" className="hover:text-[#B45309] transition-colors cursor-pointer">(208) 204-6331</a>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-5 h-5 flex items-center justify-center"><Icon name="envelope" className="w-4 h-4 text-[#B45309]" /></span>
              <a href="mailto:Southernkole@gmail.com" className="hover:text-[#B45309] transition-colors cursor-pointer">Southernkole@gmail.com</a>
            </div>
          </div>
        </div>
      </section>

      {/* Internal Links */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50" aria-label="Related pages">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-xl font-bold text-gray-900 mb-6 text-center">Explore More</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { to: '/services', icon: 'screwdriver-wrench', label: 'Our Services', desc: 'Full list of what we offer' },
              { to: '/blog/fence-installation-cost-guide-boise-idaho', icon: 'tag', label: 'Fence Cost Guide', desc: 'Pricing by material & scope' },
              { to: '/services/handyman-faq', icon: 'circle-question', label: 'Handyman FAQ', desc: 'Common questions answered' },
              { to: '/locations/fence-installation-boise-idaho', icon: 'location-dot', label: 'Fence in Boise', desc: 'Local fence installation info' },
            ].map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="bg-white rounded-xl p-5 border border-gray-100 hover:border-[#B45309]/30 hover:shadow-sm transition-all cursor-pointer group"
              >
                <div className="w-10 h-10 bg-[#B45309]/10 rounded-lg flex items-center justify-center mb-3 group-hover:bg-[#B45309]/20 transition-colors">
                  <Icon name={link.icon} className="w-5 h-5 text-[#B45309]" />
                </div>
                <p className="font-semibold text-gray-900 text-sm">{link.label}</p>
                <p className="text-xs text-gray-500 mt-0.5">{link.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <img src="https://static.readdy.ai/image/c174e3e7fa028bcf803982b5ec8aa9e6/2ea97a783d40b0afc97fadf5f7236f78.webp" alt="Fix Right Services" width="180" height="48" className="h-12 w-auto mb-4" loading="lazy" />
              <p className="text-gray-400 text-sm">Boise&apos;s trusted experts for fence installation, handyman services, and home remodels.</p>
            </div>
            <div>
              <h3 className="font-bold text-lg mb-4">Services</h3>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><Link to="/services#fence" className="hover:text-white transition-colors cursor-pointer">Fence Installation &amp; Repair</Link></li>
                <li><Link to="/services#handyman" className="hover:text-white transition-colors cursor-pointer">Handyman Services</Link></li>
                <li><Link to="/services#bathroom" className="hover:text-white transition-colors cursor-pointer">Bathroom Remodels</Link></li>
                <li><Link to="/services#remodels" className="hover:text-white transition-colors cursor-pointer">Small Remodel Projects</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="font-bold text-lg mb-4">Company</h3>
              <ul className="space-y-2 text-sm">
                <li><Link to="/" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Home</Link></li>
                <li><Link to="/about" className="text-gray-400 hover:text-white transition-colors cursor-pointer">About Us</Link></li>
                <li><Link to="/blog" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Blog</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="font-bold text-lg mb-4">Contact</h3>
              <ul className="space-y-3 text-sm">
                <li className="flex items-start gap-2 text-gray-400">
                  <span className="w-4 h-4 flex items-center justify-center mt-0.5"><Icon name="phone" className="w-3.5 h-3.5 text-[#F59E0B]" /></span>
                  <a href="tel:+12082046331" className="hover:text-white transition-colors cursor-pointer">(208) 204-6331</a>
                </li>
                <li className="flex items-start gap-2 text-gray-400">
                  <span className="w-4 h-4 flex items-center justify-center mt-0.5"><Icon name="envelope" className="w-3.5 h-3.5 text-[#F59E0B]" /></span>
                  <a href="mailto:Southernkole@gmail.com" className="hover:text-white transition-colors cursor-pointer">Southernkole@gmail.com</a>
                </li>
                <li className="flex items-start gap-2 text-gray-400">
                  <span className="w-4 h-4 flex items-center justify-center mt-0.5"><Icon name="location-dot" className="w-3.5 h-3.5 text-[#F59E0B]" /></span>
                  <span>Serving Boise, Meridian, Nampa &amp; Caldwell</span>
                </li>
              </ul>
              <div className="flex gap-3 mt-4">
                <a href="https://www.facebook.com/profile.php?id=61582658047880" aria-label="Facebook" rel="nofollow noopener noreferrer" target="_blank" className="w-10 h-10 bg-gray-800 hover:bg-[#B45309] rounded-lg flex items-center justify-center transition-colors cursor-pointer">
                  <Icon name="facebook" className="w-5 h-5" />
                </a>
                <a href="https://www.instagram.com/fixright_services/" aria-label="Instagram" rel="nofollow noopener noreferrer" target="_blank" className="w-10 h-10 bg-gray-800 hover:bg-[#B45309] rounded-lg flex items-center justify-center transition-colors cursor-pointer">
                  <Icon name="instagram" className="w-5 h-5" />
                </a>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 flex flex-col sm:flex-row justify-between items-center gap-4 text-sm text-gray-400">
            <p>&copy; 2025 Fix Right Services. All rights reserved.</p>
            <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors cursor-pointer">Website Builder</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
